import { useAtom } from 'jotai';
import { useCallback } from 'react';

import {
  type OrderAnalytics,
  type PhoneVerificationStep,
  extractOrderDataFromAnalytics,
  phoneVerificationAtom,
} from '$business/atoms/phone-verification';

export function usePhoneVerificationAtom() {
  const [state, setState] = useAtom(phoneVerificationAtom);

  const openPhoneVerification = useCallback(
    (params: { phoneNumber: string; orderAnalytics: OrderAnalytics; onRedirect: () => void }) => {
      setState({
        isOpen: true,
        step: 'prompt',
        phoneNumber: params.phoneNumber,
        isEditingPhone: false,
        orderData: extractOrderDataFromAnalytics(params.orderAnalytics),
        pendingRedirect: params.onRedirect,
      });
    },
    [setState],
  );

  const setStep = useCallback(
    (step: PhoneVerificationStep) => {
      setState((prev) => ({ ...prev, step }));
    },
    [setState],
  );

  const setPhoneNumber = useCallback(
    (phoneNumber: string) => {
      setState((prev) => ({ ...prev, phoneNumber }));
    },
    [setState],
  );

  const setIsEditingPhone = useCallback(
    (isEditingPhone: boolean) => {
      setState((prev) => ({ ...prev, isEditingPhone }));
    },
    [setState],
  );

  const close = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: false,
      step: 'prompt',
      isEditingPhone: false,
    }));
  }, [setState]);

  const skipAndRedirect = useCallback(() => {
    const redirect = state.pendingRedirect;
    setState({
      isOpen: false,
      step: 'prompt',
      phoneNumber: '',
      isEditingPhone: false,
      orderData: null,
      pendingRedirect: null,
    });
    // Execute the pending redirect after closing
    redirect?.();
  }, [state.pendingRedirect, setState]);

  const completeAndRedirect = useCallback(() => {
    const redirect = state.pendingRedirect;
    setState({
      isOpen: false,
      step: 'prompt',
      phoneNumber: '',
      isEditingPhone: false,
      orderData: null,
      pendingRedirect: null,
    });
    // Execute the pending redirect after closing
    redirect?.();
  }, [state.pendingRedirect, setState]);

  return {
    ...state,
    openPhoneVerification,
    setStep,
    setPhoneNumber,
    setIsEditingPhone,
    close,
    skipAndRedirect,
    completeAndRedirect,
  };
}
